import { useTranslation } from 'react-i18next';
import { Grid, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import moment from 'moment';
import 'moment-timezone';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { OrderPageIntent } from '../OrderPageIntent';
import { AccountData, CourseSessionOut } from '../../api';
import { MULTIPLE_TICKETS_DETAILS_FIELD_NAME } from '../core/pricing';
import { getDateFormat, getTimeFormat } from '../../utils/getDateAndTimeFormat';
import TimeUtils from '../../utils/TimeUtils';
import CourseSessionsList from './dateAndTimeStep/components/CourseSessionsList';
import { asOrderPageData, type WidgetOrderPageData } from '../core/types';

type OrderFinalDetailsTableProps = {
  shouldShowDuration: boolean;
  orderPageIntent: OrderPageIntent;
  orderPageState: {
    dateAndTimeDetails: {
      date: string | null;
      time?: number | null;
    };
    details: {
      persons: number;
      duration: number;
      custom_details?: {
        workshopType?: string;
        [MULTIPLE_TICKETS_DETAILS_FIELD_NAME]?: Record<string, number>;
      };
    };
    orderPage: WidgetOrderPageData;
  };
  account: AccountData;
  courseSessions?: CourseSessionOut[];
};

export const OrderFinalDetailsTable = ({
  orderPageIntent,
  shouldShowDuration,
  orderPageState,
  account,
  courseSessions,
}: OrderFinalDetailsTableProps) => {
  const { t } = useTranslation();
  const language = useLanguage();

  if (orderPageIntent === OrderPageIntent.BUY_PASS || orderPageIntent === OrderPageIntent.MONETARY_GIFT_CARD) {
    return null;
  }
  const timeFormat = getTimeFormat(account);
  const { longDateFormat } = getDateFormat(account);
  const bookableOrderPage = asOrderPageData(orderPageState.orderPage);
  const objectWorkShopType = bookableOrderPage?.objects?.find((x: any) => x.name === 'workshopType');
  const selectedWorkshopType = orderPageState.details.custom_details?.workshopType;
  const objectMultipleTickets = bookableOrderPage?.objects?.find((x: any) => x.type === 'multiple_tickets_price');
  const multipleTicketsDetails = orderPageState.details.custom_details?.[MULTIPLE_TICKETS_DETAILS_FIELD_NAME];
  // @ts-ignore
  const multipleTickets = objectMultipleTickets?.tickets?.find((x: any) => x.id === selectedWorkshopType);
  const renderTableRow = (label: string, value: React.ReactNode) => (
    <TableRow
      sx={{
        '& td': {
          border: 'none',
          padding: '0px 16px',
        },
      }}>
      <TableCell width="80px">
        <OrderPageTypography variant={'h6'} bold>
          {label}
        </OrderPageTypography>
      </TableCell>
      <TableCell>
        <OrderPageTypography variant={'h6'}>{value}</OrderPageTypography>
      </TableCell>
    </TableRow>
  );

  return (
    <Grid container flexDirection="column" gap={0.5} mt={1}>
      <OrderPageTypography variant="h3" bold>
        {t('orderdetails')}
      </OrderPageTypography>
      <TableContainer>
        <Table size="small">
          <TableBody>
            {orderPageIntent === OrderPageIntent.BUY_NOW &&
              (courseSessions && courseSessions.length > 0 ? (
                <TableRow
                  sx={{
                    '& td': {
                      border: 'none',
                      padding: '0px 16px',
                    },
                  }}>
                  <TableCell colSpan={2}>
                    <CourseSessionsList
                      courseSessions={courseSessions}
                      longDateFormat={longDateFormat}
                      timeFormat={timeFormat}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {renderTableRow(
                    t('order_page.date'),
                    moment(orderPageState.dateAndTimeDetails.date).format(longDateFormat),
                  )}
                  {renderTableRow(
                    t('order_page.time', {
                      activityLabelSingular: account?.activity_label_singular?.[language] || '',
                    }),
                    moment
                      .unix(orderPageState.dateAndTimeDetails?.time || 0)
                      .tz('utc')
                      .format(timeFormat),
                  )}
                </>
              ))}
            {renderTableRow(t('order_page.persons'), orderPageState.details.persons)}
            {shouldShowDuration &&
              renderTableRow(
                t('order_page.duration', {
                  activityLabelSingular: account?.activity_label_singular?.[language] || '',
                }),
                TimeUtils.formatDuration(orderPageState.details.duration, t),
              )}
            {objectWorkShopType &&
              selectedWorkshopType &&
              renderTableRow(
                t('order_page.workshop'),
                // @ts-ignore
                objectWorkShopType.options?.find((x: any) => x.value === selectedWorkshopType)?.label[language],
              )}
            {objectMultipleTickets && multipleTicketsDetails && (
              <TableRow>
                <TableCell>
                  <OrderPageTypography bold>{t('order_page.workshopChoice')}</OrderPageTypography>
                </TableCell>
                <TableCell>
                  <Grid container flexDirection="column" gap={1}>
                    {Object.entries(multipleTicketsDetails).map(([key, value]) => (
                      <Grid container key={key} gap={1}>
                        <OrderPageTypography>{multipleTickets}:</OrderPageTypography>
                        <OrderPageTypography>{value}</OrderPageTypography>
                      </Grid>
                    ))}
                  </Grid>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};
